import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  HardDrive,
  RefreshCw,
  Clock,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  RotateCcw,
  Ban,
  FileVideo,
  Folder,
  Layers,
  ArrowRight,
  HelpCircle,
  Video,
  ListVideo,
} from 'lucide-react';
import { Header } from '../components/Header.js';
import { EduCard } from '../components/EduCard.js';
import { EduButton } from '../components/EduButton.js';
import { EduBadge } from '../components/EduBadge.js';
import { api } from '../services/api.js';
import { TransferBatch, TransferJob, TransferJobStatus } from '../types/index.js';

export const Transfers: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialTab = searchParams.get('provider') === 'youtube' ? 'youtube' : 'google_drive';
  const [activeTab, setActiveTab] = useState<'google_drive' | 'youtube'>(initialTab);

  const [batches, setBatches] = useState<TransferBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedBatchId, setExpandedBatchId] = useState<string | null>(null);
  const [batchJobs, setBatchJobs] = useState<Record<string, TransferJob[]>>({});
  const [loadingJobs, setLoadingJobs] = useState<Record<string, boolean>>({});
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    const providerParam = searchParams.get('provider');
    if (providerParam === 'youtube' && activeTab !== 'youtube') {
      setActiveTab('youtube');
    } else if (providerParam !== 'youtube' && activeTab === 'youtube' && !providerParam) {
      setActiveTab('google_drive');
    }
  }, [searchParams]);

  const handleTabChange = (tab: 'google_drive' | 'youtube') => {
    setActiveTab(tab);
    setExpandedBatchId(null);
    setBatchJobs({});
    setSearchParams(tab === 'youtube' ? { provider: 'youtube' } : {});
  };

  const loadBatches = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const res =
        activeTab === 'youtube'
          ? await api.getYouTubeTransferBatches()
          : await api.getTransferBatches();
      setBatches(res.items || []);

      // If a batch is expanded, reload its jobs too
      if (expandedBatchId) {
        loadBatchDetails(expandedBatchId, true);
      }
    } catch (err) {
      console.error('Erro ao carregar lotes de transferência:', err);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const loadBatchDetails = async (batchId: string, silent = false) => {
    try {
      if (!silent) {
        setLoadingJobs((prev) => ({ ...prev, [batchId]: true }));
      }
      const res =
        activeTab === 'youtube'
          ? await api.getYouTubeTransferBatch(batchId)
          : await api.getTransferBatch(batchId);
      if (res && res.jobs) {
        setBatchJobs((prev) => ({ ...prev, [batchId]: res.jobs }));
      }
    } catch (err) {
      console.error(`Erro ao carregar detalhes do lote ${batchId}:`, err);
    } finally {
      if (!silent) {
        setLoadingJobs((prev) => ({ ...prev, [batchId]: false }));
      }
    }
  };

  useEffect(() => {
    loadBatches();

    // Auto-poll every 3.5 seconds if there's any active batch in progress or queued
    const interval = setInterval(() => {
      const hasActive = batches.some((b) => b.status === 'in_progress' || b.status === 'queued');
      if (hasActive) {
        loadBatches(true);
      }
    }, 3500);

    return () => clearInterval(interval);
  }, [expandedBatchId, batches, activeTab]);

  const toggleExpand = (batchId: string) => {
    if (expandedBatchId === batchId) {
      setExpandedBatchId(null);
    } else {
      setExpandedBatchId(batchId);
      if (!batchJobs[batchId]) {
        loadBatchDetails(batchId);
      }
    }
  };

  const handleCancelBatch = async (batchId: string) => {
    if (!confirm('Deseja cancelar as transferências pendentes deste lote?')) return;
    try {
      setActionLoading(`cancel_${batchId}`);
      if (activeTab === 'youtube') {
        await api.cancelYouTubeTransferBatch(batchId);
      } else {
        await api.cancelTransferBatch(batchId);
      }
      await loadBatches(true);
      if (expandedBatchId === batchId) {
        await loadBatchDetails(batchId, true);
      }
    } catch (err: any) {
      alert(err.message || 'Falha ao cancelar transferência.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRetry = async (batchOrJobId: string, batchId: string) => {
    try {
      setActionLoading(`retry_${batchOrJobId}`);
      if (activeTab === 'youtube') {
        await api.retryYouTubeTransfer(batchOrJobId);
      } else {
        await api.retryTransfer(batchOrJobId);
      }
      await loadBatches(true);
      await loadBatchDetails(batchId, true);
    } catch (err: any) {
      alert(err.message || 'Falha ao retentar transferência.');
    } finally {
      setActionLoading(null);
    }
  };

  const formatBytes = (bytes: number): string => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
  };

  const getBatchBadge = (status: TransferBatch['status']) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-green-50 text-status-success border border-green-200">
            <CheckCircle2 size={13} /> Concluído
          </span>
        );
      case 'completed_with_errors':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200">
            <AlertTriangle size={13} /> Concluído com Erros
          </span>
        );
      case 'in_progress':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-50 text-status-info border border-blue-200">
            <RefreshCw size={13} className="animate-spin text-status-info" /> Transferindo
          </span>
        );
      case 'queued':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-slate-100 text-txt-secondary border border-slate-200">
            <Clock size={13} /> Na Fila
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-slate-100 text-txt-muted border border-slate-200">
            <Ban size={13} /> Cancelado
          </span>
        );
      case 'failed':
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-50 text-status-error border border-red-200">
            <XCircle size={13} /> Falha
          </span>
        );
    }
  };

  const getJobBadge = (status: TransferJobStatus) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold bg-green-50 text-status-success border border-green-200">
            <CheckCircle2 size={11} /> Concluído
          </span>
        );
      case 'uploading':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold bg-blue-50 text-status-info border border-blue-200 animate-pulse">
            <RefreshCw size={11} className="animate-spin" /> Enviando MP4
          </span>
        );
      case 'uploaded':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold bg-blue-50 text-status-info border border-blue-200">
            <CheckCircle2 size={11} /> Arquivo Enviado
          </span>
        );
      case 'processing_youtube':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">
            <RefreshCw size={11} className="animate-spin" /> Processando no YouTube
          </span>
        );
      case 'creating_playlist':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold bg-purple-50 text-purple-700 border border-purple-200">
            <ListVideo size={11} /> Criando Playlist
          </span>
        );
      case 'adding_to_playlist':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold bg-purple-50 text-purple-700 border border-purple-200">
            <ListVideo size={11} className="animate-spin" /> Adicionando à Playlist
          </span>
        );
      case 'verifying':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold bg-blue-50 text-status-info border border-blue-200">
            <RefreshCw size={11} className="animate-spin" /> Verificando
          </span>
        );
      case 'preparing':
      case 'checking_destination':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-blue-50 text-status-info border border-blue-200">
            <RefreshCw size={11} className="animate-spin" /> Preparando
          </span>
        );
      case 'skipped':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-slate-100 text-txt-muted border border-slate-200" title="Arquivo idêntico já transferido">
            <CheckCircle2 size={11} /> Já Enviado (Ignorado)
          </span>
        );
      case 'queued':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-slate-100 text-txt-secondary border border-slate-200">
            <Clock size={11} /> Na Fila
          </span>
        );
      case 'awaiting_authorization':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
            <AlertTriangle size={11} /> Aguardando Autorização
          </span>
        );
      case 'quota_exceeded':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
            <AlertTriangle size={11} /> Quota Excedida
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-slate-100 text-txt-muted border border-slate-200">
            <Ban size={11} /> Cancelado
          </span>
        );
      case 'failed':
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold bg-red-50 text-status-error border border-red-200">
            <XCircle size={11} /> Falha
          </span>
        );
    }
  };

  return (
    <div>
      <Header
        title="Fila de Transferências"
        subtitle="Acompanhe o envio assíncrono de videoaulas para o Google Drive e YouTube"
        action={
          <EduButton
            variant="secondary"
            size="sm"
            onClick={() => loadBatches()}
            icon={<RefreshCw size={14} className={loading ? 'animate-spin' : ''} />}
          >
            Atualizar
          </EduButton>
        }
      />

      <div className="p-8 max-w-7xl mx-auto space-y-6">
        {/* Navegação por Abas de Provedor (Seção 20) */}
        <div className="flex items-center gap-2 border-b border-bdr pb-1">
          <button
            onClick={() => handleTabChange('google_drive')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-t-lg text-sm font-semibold transition-all border-b-2 ${
              activeTab === 'google_drive'
                ? 'border-brand-primary text-brand-primary bg-orange-50/50'
                : 'border-transparent text-txt-secondary hover:text-txt-primary hover:bg-surface-secondary'
            }`}
          >
            <HardDrive size={16} /> Google Drive
          </button>

          <button
            onClick={() => handleTabChange('youtube')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-t-lg text-sm font-semibold transition-all border-b-2 ${
              activeTab === 'youtube'
                ? 'border-brand-primary text-brand-primary bg-orange-50/50'
                : 'border-transparent text-txt-secondary hover:text-txt-primary hover:bg-surface-secondary'
            }`}
          >
            <svg className="w-4 h-4 text-[#FF0000]" viewBox="0 0 24 24" fill="currentColor">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
            </svg>
            YouTube
          </button>
        </div>

        {/* Lista de Lotes de Transferência */}
        {loading && batches.length === 0 ? (
          <div className="p-16 text-center text-xs text-txt-muted flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full animate-spin" />
            <span>Carregando transferências...</span>
          </div>
        ) : batches.length === 0 ? (
          <EduCard>
            <div className="text-center py-12">
              {activeTab === 'youtube' ? (
                <div className="w-12 h-12 rounded-full bg-red-50 text-[#FF0000] flex items-center justify-center mx-auto mb-3">
                  <Video size={24} />
                </div>
              ) : (
                <HardDrive size={40} className="mx-auto text-txt-muted/60 mb-3" />
              )}
              <h3 className="text-base font-semibold text-txt-primary">
                Nenhuma transferência {activeTab === 'youtube' ? 'para o YouTube' : 'para o Google Drive'} encontrada
              </h3>
              <p className="text-xs text-txt-secondary mt-1 max-w-md mx-auto">
                {activeTab === 'youtube'
                  ? 'Você ainda não solicitou exportações para o seu canal do YouTube. Acesse Meus Projetos para selecionar e exportar videoaulas.'
                  : 'Você ainda não enviou videoaulas para o seu Google Drive. Acesse a lista de projetos para iniciar transferências.'}
              </p>
              <EduButton
                variant="primary"
                size="sm"
                className="mt-5"
                onClick={() => navigate('/projetos')}
                icon={<ArrowRight size={14} />}
              >
                Ir para Meus Projetos
              </EduButton>
            </div>
          </EduCard>
        ) : (
          <div className="space-y-4">
            {batches.map((batch) => {
              const isExpanded = expandedBatchId === batch.id;
              const jobs = batchJobs[batch.id] || [];
              const isLoadingJobs = loadingJobs[batch.id] || false;

              // Progress calculation
              const totalItems = batch.totalFiles || 1;
              const doneItems = (batch.completedFiles || 0) + (batch.skippedFiles || 0);
              const progressPct = Math.min(100, Math.round((doneItems / totalItems) * 100));

              return (
                <div
                  key={batch.id}
                  className="bg-surface border border-bdr rounded-xl shadow-xs overflow-hidden transition-all"
                >
                  {/* Batch Header Bar */}
                  <div
                    className="p-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 cursor-pointer hover:bg-surface-secondary/40 transition-colors"
                    onClick={() => toggleExpand(batch.id)}
                  >
                    <div className="flex items-start gap-3.5 flex-1 min-w-0">
                      <button
                        className="mt-0.5 p-1 text-txt-muted hover:text-brand-primary rounded transition-colors"
                        title={isExpanded ? 'Recolher detalhes' : 'Expandir detalhes'}
                      >
                        {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                      </button>

                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-mono text-xs font-bold text-txt-primary">
                            #{batch.id.slice(0, 16)}
                          </span>
                          {getBatchBadge(batch.status)}
                          <span className="text-[11px] text-txt-muted flex items-center gap-1 font-mono">
                            <Clock size={12} />
                            {new Date(batch.createdAt).toLocaleDateString('pt-BR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </span>
                        </div>

                        <div className="flex items-center gap-3 text-xs text-txt-secondary flex-wrap">
                          <span className="font-semibold text-txt-primary flex items-center gap-1">
                            <Layers size={13} className="text-brand-primary" />
                            {batch.projectNames?.join(', ') || `${batch.projectIds?.length || 1} projeto(s)`}
                          </span>
                          <span>•</span>
                          {activeTab === 'youtube' ? (
                            <span className="flex items-center gap-1 text-txt-primary font-semibold">
                              <svg className="w-3.5 h-3.5 text-[#FF0000]" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                              </svg>
                              Canal: <strong>{batch.channelTitle || 'YouTube'}</strong>
                            </span>
                          ) : (
                            <span className="flex items-center gap-1">
                              <Folder size={13} className="text-txt-muted" />
                              Drive: <strong>{batch.rootFolderName || 'EduRender'}</strong>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Right side stats & progress bar */}
                    <div className="flex items-center gap-6 w-full md:w-auto justify-between md:justify-end border-t md:border-t-0 pt-3 md:pt-0 border-bdr/60">
                      <div className="text-right">
                        <div className="flex items-center gap-2 text-xs font-semibold text-txt-primary justify-end">
                          <span>
                            {doneItems} / {batch.totalFiles} vídeos
                          </span>
                          <span className="font-mono text-brand-primary">({progressPct}%)</span>
                        </div>

                        {/* Progress Bar */}
                        <div className="w-36 h-2 bg-slate-100 rounded-full overflow-hidden mt-1.5 border border-bdr">
                          <div
                            className={`h-full transition-all duration-300 ${
                              batch.status === 'completed'
                                ? 'bg-status-success'
                                : batch.status === 'failed' || batch.status === 'completed_with_errors'
                                ? 'bg-amber-500'
                                : activeTab === 'youtube'
                                ? 'bg-red-600'
                                : 'bg-brand-primary'
                            }`}
                            style={{ width: `${progressPct}%` }}
                          />
                        </div>

                        <p className="text-[11px] text-txt-muted mt-1 font-mono">
                          {formatBytes(batch.transferredBytes || 0)} / {formatBytes(batch.totalBytes || 0)}
                        </p>
                      </div>

                      {/* Batch Action Buttons */}
                      <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                        {batch.status === 'in_progress' || batch.status === 'queued' ? (
                          <button
                            onClick={() => handleCancelBatch(batch.id)}
                            disabled={actionLoading === `cancel_${batch.id}`}
                            className="p-1.5 text-txt-muted hover:text-status-error hover:bg-red-50 rounded-lg transition-colors"
                            title="Cancelar transferência deste lote"
                          >
                            <Ban size={16} />
                          </button>
                        ) : (batch.failedFiles > 0 || batch.status === 'failed' || batch.status === 'completed_with_errors') ? (
                          <button
                            onClick={() => handleRetry(batch.id, batch.id)}
                            disabled={actionLoading === `retry_${batch.id}`}
                            className="p-1.5 text-brand-primary hover:text-brand-hover hover:bg-orange-50 rounded-lg transition-colors"
                            title="Tentar novamente arquivos com erro"
                          >
                            <RotateCcw size={16} />
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Sub-table with individual Lesson Videos */}
                  {isExpanded && (
                    <div className="border-t border-bdr bg-surface-secondary/40 p-4">
                      {isLoadingJobs ? (
                        <div className="py-6 text-center text-xs text-txt-muted flex items-center justify-center gap-2">
                          <div className="w-4 h-4 border-2 border-brand-primary border-t-transparent rounded-full animate-spin" />
                          <span>Carregando tarefas individuais...</span>
                        </div>
                      ) : jobs.length === 0 ? (
                        <p className="text-xs text-txt-muted text-center py-4">
                          Nenhuma tarefa individual encontrada neste lote.
                        </p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-left text-xs">
                            <thead>
                              <tr className="border-b border-bdr text-txt-muted font-semibold bg-surface-secondary/80">
                                <th className="py-2.5 px-3">Projeto</th>
                                <th className="py-2.5 px-3">Aula</th>
                                {activeTab === 'youtube' && <th className="py-2.5 px-3">Playlist</th>}
                                <th className="py-2.5 px-3">Arquivo MP4</th>
                                <th className="py-2.5 px-3">Tamanho</th>
                                <th className="py-2.5 px-3">Progresso</th>
                                <th className="py-2.5 px-3">Status</th>
                                <th className="py-2.5 px-3">Tentativas</th>
                                <th className="py-2.5 px-3 text-right">Ações</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-bdr/60">
                              {jobs.map((job) => {
                                const jobPct =
                                  job.status === 'completed'
                                    ? 100
                                    : job.fileSize > 0
                                    ? Math.min(100, Math.round(((job.transferredBytes || 0) / job.fileSize) * 100))
                                    : 0;

                                return (
                                  <tr key={job.id} className="hover:bg-surface transition-colors">
                                    <td className="py-2.5 px-3 font-medium text-txt-primary truncate max-w-[140px]">
                                      {job.projectName || '—'}
                                    </td>
                                    <td className="py-2.5 px-3 text-txt-secondary truncate max-w-[160px]">
                                      {job.lessonTitle || `Aula ${job.lessonOrder || 1}`}
                                    </td>
                                    {activeTab === 'youtube' && (
                                      <td className="py-2.5 px-3 font-medium text-txt-primary">
                                        {job.playlistId ? (
                                          <a
                                            href={`https://www.youtube.com/playlist?list=${job.playlistId}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-brand-primary hover:underline inline-flex items-center gap-1 font-mono text-[11px]"
                                          >
                                            <ListVideo size={12} /> Playlist
                                            <ExternalLink size={10} />
                                          </a>
                                        ) : (
                                          <span className="text-txt-muted text-[11px]">Pendente</span>
                                        )}
                                      </td>
                                    )}
                                    <td className="py-2.5 px-3 font-mono text-[11px] text-txt-primary flex items-center gap-1.5">
                                      <FileVideo size={13} className="text-brand-primary shrink-0" />
                                      <span className="truncate max-w-[180px]" title={job.fileName}>
                                        {job.fileName}
                                      </span>
                                    </td>
                                    <td className="py-2.5 px-3 font-mono text-txt-muted">
                                      {formatBytes(job.fileSize)}
                                    </td>
                                    <td className="py-2.5 px-3">
                                      <div className="flex items-center gap-2">
                                        <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                          <div
                                            className={`h-full transition-all duration-200 ${
                                              activeTab === 'youtube' ? 'bg-red-600' : 'bg-brand-primary'
                                            }`}
                                            style={{ width: `${jobPct}%` }}
                                          />
                                        </div>
                                        <span className="font-mono text-[10px] text-txt-muted">
                                          {jobPct}%
                                        </span>
                                      </div>
                                    </td>
                                    <td className="py-2.5 px-3">
                                      {getJobBadge(job.status)}
                                      {job.errorMessage && (
                                        <p className="text-[10px] text-status-error truncate max-w-[180px] mt-0.5" title={job.errorMessage}>
                                          {job.errorMessage}
                                        </p>
                                      )}
                                    </td>
                                    <td className="py-2.5 px-3 font-mono text-txt-muted">
                                      {job.attemptCount} / {job.maxAttempts}
                                    </td>
                                    <td className="py-2.5 px-3 text-right">
                                      <div className="flex items-center justify-end gap-1.5">
                                        {/* Links do YouTube */}
                                        {job.youtubeVideoId && (
                                          <a
                                            href={`https://www.youtube.com/watch?v=${job.youtubeVideoId}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors inline-flex items-center gap-0.5 text-[11px] font-semibold"
                                            title="Assistir vídeo privado no YouTube"
                                          >
                                            <Video size={12} /> Ver Vídeo <ExternalLink size={10} />
                                          </a>
                                        )}

                                        {/* Link do Drive */}
                                        {job.driveFileUrl && (
                                          <a
                                            href={job.driveFileUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="p-1 text-brand-primary hover:text-brand-hover hover:bg-orange-50 rounded transition-colors inline-flex items-center gap-0.5 text-[11px] font-semibold"
                                            title="Abrir arquivo no Google Drive"
                                          >
                                            <ExternalLink size={12} /> Abrir
                                          </a>
                                        )}

                                        {/* Retentativas */}
                                        {(job.status === 'failed' || job.status === 'quota_exceeded') && (
                                          <button
                                            onClick={() => handleRetry(job.id, batch.id)}
                                            disabled={actionLoading === `retry_${job.id}`}
                                            className="p-1 text-txt-muted hover:text-brand-primary hover:bg-orange-50 rounded transition-colors inline-flex items-center gap-0.5 text-[11px]"
                                            title="Retentar transferência deste vídeo"
                                          >
                                            <RotateCcw size={12} /> Retentar
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
